<template>
  <div class="provisioning-run">
    <div v-if="loading" class="provisioning-run__loading">Loading run…</div>

    <div v-else-if="loadError" class="provisioning-run__error">{{ loadError }}</div>

    <template v-else-if="run">
      <header class="provisioning-run__header">
        <div>
          <h1 class="provisioning-run__title">
            {{ run.project_title || `Provisioning run #${run.id}` }}
          </h1>
          <p class="provisioning-run__meta">
            <span>{{ run.blueprint_key }}</span>
            <span v-if="run.external_ref"> · {{ run.external_ref }}</span>
          </p>
        </div>

        <span class="provisioning-run__status" :class="`is-${run.status}`">
          {{ statusLabel(run.status) }}
        </span>
      </header>

      <p v-if="run.error_message" class="provisioning-run__failure">
        {{ run.error_message }}
      </p>

      <div class="provisioning-run__actions">
        <button
          v-if="run.status === 'failed'"
          class="btn btn-primary"
          :disabled="busy"
          @click="act('resume')"
        >
          Resume from failed step
        </button>

        <button v-if="canRollBack" class="btn btn-danger" :disabled="busy" @click="confirmRollback">
          Roll back
        </button>
      </div>

      <ol class="provisioning-run__steps">
        <li
          v-for="step in steps"
          :key="step.id"
          class="provisioning-step"
          :class="`is-${step.status}`"
        >
          <div class="provisioning-step__head">
            <span class="provisioning-step__ordinal">{{ step.ordinal }}</span>
            <span class="provisioning-step__key">{{ step.step_key }}</span>
            <span class="provisioning-step__status">{{ statusLabel(step.status) }}</span>
          </div>

          <p class="provisioning-step__reverse">
            Reverse: <strong>{{ reverseLabel(step.reverse_kind) }}</strong>
            <span v-if="step.attempts > 1"> · {{ step.attempts }} attempts</span>
          </p>

          <p v-if="step.error_message" class="provisioning-step__error">{{ step.error_message }}</p>

          <!--
            The undo payload is shown deliberately: when an automated rollback
            fails, this is exactly what a human needs to finish the unwind by
            hand. The machine failing must not mean the information is lost.
          -->
          <details v-if="hasPayload(step.undo_json)" class="provisioning-step__payload">
            <summary>Undo payload</summary>
            <pre>{{ pretty(step.undo_json) }}</pre>
          </details>

          <details v-if="hasPayload(step.output_json)" class="provisioning-step__payload">
            <summary>Output</summary>
            <pre>{{ pretty(step.output_json) }}</pre>
          </details>
        </li>
      </ol>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import commonProps from '@apexl/icarus-core/src/components/_properties/common';

// Mounted at `/projects/provisioning-runs/{id}` via
// `app\module\projects\Controller\ProjectsDisplayController::show()`. Owns its
// own fetching/state, mirroring `Servers/ServerDetailPanel.vue`.
const props = defineProps({
  ...commonProps,
  runId: {
    type: [Number, String],
    default: 0,
  },
  apiBase: {
    type: String,
    default: '/api/v1/projects',
  },
});

const api = useApi();

const run = ref(null);
const steps = ref([]);
const loading = ref(true);
const busy = ref(false);
const loadError = ref(null);
let pollHandle = null;

const RUN_STATUS_LABELS = {
  awaiting_parameters: 'Awaiting parameters',
  pending: 'Pending',
  running: 'Running',
  awaiting_approval: 'Awaiting approval',
  failed: 'Failed',
  completed: 'Completed',
  rolling_back: 'Rolling back',
  rolled_back: 'Rolled back',
  rollback_failed: 'Rollback failed',
};

const STEP_STATUS_LABELS = {
  pending: 'Pending',
  claimed: 'Claimed',
  running: 'Running',
  awaiting_approval: 'Awaiting approval',
  succeeded: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
  reversing: 'Reversing',
  reversed: 'Reversed',
  reverse_failed: 'Reverse failed',
};

const REVERSE_LABELS = {
  pure: 'Reversible',
  compensating: 'Compensating',
  terminal: 'Irreversible — needs approval',
};

const isSettled = computed(() =>
  ['completed', 'failed', 'rolled_back', 'rollback_failed'].includes(run.value?.status),
);

const canRollBack = computed(() =>
  ['completed', 'failed', 'rollback_failed'].includes(run.value?.status),
);

const statusLabel = (status) => RUN_STATUS_LABELS[status] || STEP_STATUS_LABELS[status] || status;
const reverseLabel = (kind) => REVERSE_LABELS[kind] || kind;
const hasPayload = (payload) => !!payload && Object.keys(payload).length > 0;
const pretty = (payload) => JSON.stringify(payload, null, 2);

async function load(quiet = false) {
  if (!quiet) loading.value = true;
  try {
    const data = await api.get(`${props.apiBase}/provisioning-runs/${props.runId}`);
    run.value = data.run;
    steps.value = data.steps || [];
    loadError.value = null;
  } catch (e) {
    loadError.value = e?.response?.data?.error || 'Could not load this run.';
  } finally {
    loading.value = false;
  }
}

function confirmRollback() {
  const message =
    'Roll back this run? Every completed step will be reversed in order, newest first. '
    + 'Steps marked irreversible cannot be undone — their compensating action runs instead.';
  if (window.confirm(message)) act('rollback');
}

async function act(action) {
  busy.value = true;
  try {
    const data = await api.post(`${props.apiBase}/provisioning-runs/${props.runId}/${action}`);
    run.value = data.run;
    if (data.steps) steps.value = data.steps;
    loadError.value = null;
  } catch (e) {
    loadError.value = e?.response?.data?.error || `Could not ${action} this run.`;
  } finally {
    busy.value = false;
    load(true);
  }
}

onMounted(() => {
  load();
  // The sweeper advances runs out of band, so poll while there is still work
  // to do. Cheap, and it avoids adding a socket for a cron-driven process.
  pollHandle = setInterval(() => {
    if (!isSettled.value) load(true);
  }, 5000);
});

onBeforeUnmount(() => {
  if (pollHandle) clearInterval(pollHandle);
});
</script>
